import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { BoltSlashIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import { ROUTES } from '@/constants/routes';
import DateInput from '@/components/common/DateInput';
import OrgTreeSelect from '@/components/common/OrgTreeSelect';
import { EventLocationMapModal } from '@/components/maps/EventLocationMapModal';
import {
  fetchUnplugEvents,
  type UnplugEvent,
  type UnplugEventType,
} from '@/services/vehicles/unplugEventsApi';

const PAGE_SIZE = 50;

function localYmd(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

type AppliedFilters = {
  fromDate: string;
  toDate: string;
  organizationId: string;
  eventType: UnplugEventType | '';
  registrationNumber: string;
  offset: number;
  requestId: number;
};

export default function UnplugEventsPage() {
  const { t } = useTranslation();
  const now = useMemo(() => new Date(), []);
  const defaultFrom = useMemo(
    () => localYmd(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30)),
    [now]
  );
  const defaultTo = useMemo(() => localYmd(now), [now]);

  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);
  const [organizationId, setOrganizationId] = useState('');
  const [eventType, setEventType] = useState<UnplugEventType | ''>('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [applied, setApplied] = useState<AppliedFilters>({
    fromDate: defaultFrom,
    toDate: defaultTo,
    organizationId: '',
    eventType: '',
    registrationNumber: '',
    offset: 0,
    requestId: 0,
  });
  const [mapEvent, setMapEvent] = useState<UnplugEvent | null>(null);

  const applyFilters = () => {
    setApplied((prev) => ({
      fromDate,
      toDate,
      organizationId,
      eventType,
      registrationNumber: registrationNumber.trim(),
      offset: 0,
      requestId: prev.requestId + 1,
    }));
  };

  const goPage = (offset: number) => {
    setApplied((prev) => ({ ...prev, offset, requestId: prev.requestId + 1 }));
  };

  const { data, isLoading, isFetching, error } = useQuery(
    ['unplug-events', applied],
    () =>
      fetchUnplugEvents({
        from: applied.fromDate,
        to: applied.toDate,
        organizationId: applied.organizationId,
        eventType: applied.eventType,
        registrationNumber: applied.registrationNumber,
        limit: PAGE_SIZE,
        offset: applied.offset,
      }),
    {
      enabled: Boolean(applied.fromDate && applied.toDate),
      staleTime: 0,
      cacheTime: 0,
    }
  );

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pageFrom = total === 0 ? 0 : applied.offset + 1;
  const pageTo = applied.offset + rows.length;
  const canPrev = applied.offset > 0;
  const canNext = applied.offset + rows.length < total;
  const pageNumber = Math.floor(applied.offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const busy = isLoading || isFetching;

  const pager = (canPrev || canNext) && (
    <div className="flex items-center gap-2">
      <button
        type="button"
        disabled={!canPrev || busy}
        onClick={() => goPage(Math.max(0, applied.offset - PAGE_SIZE))}
        className="rounded-md border border-borderDefault px-2 py-1 disabled:opacity-50"
      >
        {t('common.previous')}
      </button>
      <span>
        {t('unplugEvents.pageOf', { page: pageNumber, pages: pageCount })}
      </span>
      <button
        type="button"
        disabled={!canNext || busy}
        onClick={() => goPage(applied.offset + PAGE_SIZE)}
        className="rounded-md border border-borderDefault px-2 py-1 disabled:opacity-50"
      >
        {t('common.next')}
      </button>
    </div>
  );

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-textDefault flex items-center gap-2">
            <BoltSlashIcon className="h-7 w-7 text-red-500" />
            {t('unplugEvents.title')}
          </h1>
          <p className="text-sm text-textSecondary mt-1">{t('unplugEvents.subtitle')}</p>
        </div>
        <Link to={ROUTES.DASHBOARD} className="text-sm text-primary hover:underline">
          {t('unplugEvents.backToDashboard')}
        </Link>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-borderDefault bg-bgElevated p-3">
        <OrgTreeSelect
          value={organizationId}
          onChange={setOrganizationId}
          showEmptyOption
          emptyLabel={t('fleet.filterAllOrganizations')}
          variant="chip"
          chipLabel={t('fleet.organization')}
          isActive={organizationId !== ''}
          className="inline-block w-auto min-w-[180px]"
        />
        <label className="text-sm text-textSecondary">
          {t('common.from')}
          <DateInput
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="mt-1 block"
          />
        </label>
        <label className="text-sm text-textSecondary">
          {t('common.to')}
          <DateInput
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="mt-1 block"
          />
        </label>
        <label className="text-sm text-textSecondary">
          {t('unplugEvents.eventType')}
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value as UnplugEventType | '')}
            className="mt-1 block rounded-md border border-borderDefault bg-bgBase px-2 py-1.5 text-sm text-textDefault"
          >
            <option value="">{t('unplugEvents.allTypes')}</option>
            <option value="unplugged">{t('unplugEvents.unplugged')}</option>
            <option value="plugged">{t('unplugEvents.plugged')}</option>
          </select>
        </label>
        <label className="text-sm text-textSecondary flex-1 min-w-[12rem]">
          {t('unplugEvents.registrationNumber')}
          <input
            value={registrationNumber}
            onChange={(e) => setRegistrationNumber(e.target.value)}
            placeholder={t('unplugEvents.searchPlaceholder')}
            className="mt-1 block w-full rounded-md border border-borderDefault bg-bgBase px-2 py-1.5 text-sm text-textDefault"
          />
        </label>
        <button
          type="button"
          onClick={() => applyFilters()}
          disabled={busy || !fromDate || !toDate}
          className="rounded-md bg-primary px-3 py-1.5 text-sm text-white disabled:opacity-50"
        >
          {t('common.refresh')}
        </button>
      </div>

      {busy && <p className="text-sm text-textSecondary">{t('common.loading')}</p>}
      {error != null && !busy && (
        <p className="text-sm text-red-600">{t('unplugEvents.loadError')}</p>
      )}

      {!busy && error == null && rows.length === 0 && (
        <p className="rounded-lg border border-borderDefault bg-bgBase px-3 py-8 text-center text-sm text-textSecondary">
          {t('unplugEvents.empty')}
        </p>
      )}

      {rows.length > 0 && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-textSecondary">
            <span>{t('unplugEvents.showing', { from: pageFrom, to: pageTo, total })}</span>
            {pager}
          </div>
          <div className="overflow-x-auto rounded-lg border border-borderDefault">
            <table className="min-w-full text-sm">
              <thead className="bg-bgElevated text-left text-textSecondary">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('unplugEvents.occurredAt')}</th>
                  <th className="px-3 py-2 font-medium">{t('unplugEvents.eventType')}</th>
                  <th className="px-3 py-2 font-medium">{t('fleet.vehicle')}</th>
                  <th className="px-3 py-2 font-medium">{t('fleet.organization')}</th>
                  <th className="px-3 py-2 font-medium">{t('unplugEvents.address')}</th>
                  <th className="px-3 py-2 font-medium">{t('unplugEvents.email')}</th>
                  <th className="px-3 py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const hasPos = row.lat != null && row.lng != null;
                  return (
                    <tr key={row.id} className="border-t border-borderDefault">
                      <td className="px-3 py-2 whitespace-nowrap text-textDefault">
                        {new Date(row.occurredAt).toLocaleString(undefined, {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            row.eventType === 'unplugged'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                              : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                          }`}
                        >
                          {row.eventType === 'unplugged'
                            ? t('unplugEvents.unplugged')
                            : t('unplugEvents.plugged')}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-medium text-textDefault">
                        {row.registrationNumber}
                      </td>
                      <td className="px-3 py-2 text-textSecondary">{row.organizationName}</td>
                      <td
                        className="px-3 py-2 text-textSecondary max-w-xs truncate"
                        title={row.address || ''}
                      >
                        {row.address || '–'}
                      </td>
                      <td className="px-3 py-2 text-textSecondary">
                        {row.eventType === 'unplugged'
                          ? row.emailSentAt
                            ? t('unplugEvents.emailSent')
                            : t('unplugEvents.emailNotSent')
                          : '–'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {hasPos && (
                          <button
                            type="button"
                            onClick={() => setMapEvent(row)}
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                          >
                            <MapPinIcon className="h-4 w-4" />
                            {t('booking.vehicleMapShow')}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pager && (
            <div className="flex justify-end text-sm text-textSecondary">{pager}</div>
          )}
        </>
      )}

      {mapEvent && mapEvent.lat != null && mapEvent.lng != null && (
        <EventLocationMapModal
          title={mapEvent.registrationNumber}
          subtitle={
            mapEvent.eventType === 'unplugged'
              ? t('unplugEvents.unplugged')
              : t('unplugEvents.plugged')
          }
          lat={mapEvent.lat}
          lng={mapEvent.lng}
          occurredAt={mapEvent.occurredAt}
          address={mapEvent.address}
          onClose={() => setMapEvent(null)}
        />
      )}
    </div>
  );
}
